import {
  Cable,
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  FileSpec,
  ICommand,
  commandsState,
  useConfig
} from '@chainlit/react-client';

import Icon from '@/components/Icon';
import { useTranslation } from '@/components/i18n/Translator';
import { GitHub } from '@/components/icons/Github';
import { PaperClip } from '@/components/icons/PaperClip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { useIsMobile } from '@/hooks/use-mobile';
import { useConnectorPreferences } from '@/hooks/useConnectorPreferences';
import { useUpload } from '@/hooks/useUpload';

import { userSettingsOpenState } from '@/state/project';

import { GoogleDrive } from '@/components/icons/GoogleDrive';

import GitHubPickerButton from './GitHubPickerButton';
import { useGoogleDrivePicker } from './GoogleDriveButton';
// Waiting for approval from FON.
// import MicrosoftButton from './MicrosoftButton';

/**
 * Backend command ids are Serbian and double as the wire value, so they must
 * not be translated. This maps them to English display copy for en-US.
 */
const COMMAND_TRANSLATIONS: Record<
  string,
  { label: string; description: string }
> = {
  'Plan učenja': {
    label: 'Study plan',
    description: 'A personalized study plan for an exam'
  },
  'Flash kartice': {
    label: 'Flashcards',
    description: 'Flashcards from your uploaded materials'
  },
  Kviz: {
    label: 'Quiz',
    description: 'Multiple-choice quiz from your materials'
  },
  'Mapa uma': {
    label: 'Mind map',
    description: 'Mind map from a topic or text (upload optional)'
  }
};

// Waiting for approval from FON.
// function MicrosoftGlyph({ className }: { className?: string }) {
//   return (
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//       className={className}
//       aria-hidden
//     >
//       <rect x="2" y="2" width="9" height="9" fill="#F25022" />
//       <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
//       <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
//       <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
//     </svg>
//   );
// }

interface Props {
  disabled?: boolean;
  fileSpec: FileSpec;
  selectedCommandId?: string;
  onFileUpload: (files: File[]) => void;
  onFileUploadError: (error: string) => void;
  onRepoSelected: (repoUrl: string) => void;
  onRepoError: (error: string) => void;
  onCommandSelect: (command?: ICommand) => void;
}

export default function PlusMenu({
  disabled = false,
  fileSpec,
  selectedCommandId,
  onFileUpload,
  onFileUploadError,
  onRepoSelected,
  onRepoError,
  onCommandSelect
}: Props) {
  const { config } = useConfig();
  const commands = useRecoilValue(commandsState);
  const { enabledConnectors } = useConnectorPreferences();
  const isMobile = useIsMobile();
  const setSettingsOpen = useSetRecoilState(userSettingsOpenState);
  const { i18n } = useTranslation();
  let language = i18n.resolvedLanguage;
  try {
    language = localStorage.getItem('fongpt-language') || language;
  } catch {
    // Keep i18n's current language when local storage is unavailable.
  }
  const isEnglish = language?.toLowerCase().startsWith('en');
  const plusRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileMenuView, setMobileMenuView] = useState<'main' | 'connectors'>(
    'main'
  );
  const [, setMsOpen] = useState(false);
  const [githubOpen, setGithubOpen] = useState(false);
  // Waiting for approval from FON.
  // const suppressMsCloseUntil = useRef(0);
  const suppressGithubCloseUntil = useRef(0);

  const drive = useGoogleDrivePicker({
    disabled,
    onFileUpload,
    onFileUploadError
  });

  const upload = useUpload({
    spec: fileSpec,
    onResolved: (payloads: File[]) => onFileUpload(payloads),
    onError: onFileUploadError,
    options: { noDrag: true, noClick: true, noKeyboard: true }
  });

  const fileUploadEnabled = !!config?.features.spontaneous_file_upload?.enabled;
  const studyCommands = useMemo(
    () => commands.filter((command) => !!command.button),
    [commands]
  );

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setMenuOpen(open);
    if (!open) setMobileMenuView('main');
  }, []);

  useEffect(() => {
    if (!isMobile) setMobileMenuView('main');
  }, [isMobile]);

  const labels = isEnglish
    ? {
        add: 'Add',
        addFiles: 'Files or photos',
        connectors: 'Connectors',
        manageConnectors: 'Enable connectors in Settings',
        addDrive: 'Google Drive',
        driveHint: 'Choose files from Drive',
        addMicrosoft: 'Microsoft',
        microsoftHint: 'Choose from OneDrive or Teams',
        addGithub: 'GitHub',
        githubHint: 'Connect an account or paste a repository',
        filePickerError: 'Could not open the file picker.'
      }
    : {
        add: 'Dodaj',
        addFiles: 'Fajlovi ili fotografije',
        connectors: 'Integracije',
        manageConnectors: 'Uključi integracije u podešavanjima',
        addDrive: 'Google Drive',
        driveHint: 'Izaberi fajlove sa Drive-a',
        addMicrosoft: 'Microsoft',
        microsoftHint: 'Izaberi sa OneDrive-a ili Teams-a',
        addGithub: 'GitHub',
        githubHint: 'Poveži nalog ili nalepi repozitorijum',
        filePickerError: 'Neuspelo otvaranje birača fajlova.'
      };

  const openFilePicker = useCallback(
    (e: Event) => {
      e.preventDefault();
      setMenuOpen(false);
      setMsOpen(false);
      setGithubOpen(false);
      try {
        upload.open();
      } catch {
        onFileUploadError(labels.filePickerError);
      }
    },
    [upload, onFileUploadError, labels.filePickerError]
  );

  // Straight from the click: the Google sign-in popup is only allowed
  // inside the user gesture.
  const openDrive = useCallback(() => {
    setGithubOpen(false);
    setMsOpen(false);
    setMenuOpen(false);
    drive.open();
  }, [drive]);

  // Waiting for approval from FON.
  // const openMicrosoft = useCallback(() => {
  //   setDriveOpen(false);
  //   setGithubOpen(false);
  //   setMenuOpen(false);
  //   suppressMsCloseUntil.current = Date.now() + 500;
  //   window.setTimeout(() => setMsOpen(true), 120);
  // }, []);

  const openGithub = useCallback(() => {
    setMsOpen(false);
    setMenuOpen(false);
    suppressGithubCloseUntil.current = Date.now() + 500;
    window.setTimeout(() => setGithubOpen(true), 120);
  }, []);

  const openConnectorSettings = useCallback(
    (e: Event) => {
      e.preventDefault();
      setMenuOpen(false);
      setSettingsOpen(true);
    },
    [setSettingsOpen]
  );

  // Waiting for approval from FON.
  // const handleMsOpenChange = useCallback((open: boolean) => {
  //   if (!open && Date.now() < suppressMsCloseUntil.current) return;
  //   setMsOpen(open);
  // }, []);

  const handleGithubOpenChange = useCallback((open: boolean) => {
    if (!open && Date.now() < suppressGithubCloseUntil.current) return;
    setGithubOpen(open);
  }, []);

  const connectorOptions = (
    <>
      {enabledConnectors.googleDrive ? (
        <DropdownMenuItem
          id="attachment-menu-google-drive"
          className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
          disabled={disabled || drive.busy}
          onSelect={openDrive}
        >
          <GoogleDrive className="!size-4" />
          <div className="min-w-0">
            <div className="text-sm font-medium">{labels.addDrive}</div>
            <div className="truncate text-xs text-muted-foreground">
              {labels.driveHint}
            </div>
          </div>
        </DropdownMenuItem>
      ) : null}
      {/* Waiting for approval from FON.
      {enabledConnectors.microsoft ? (
        <DropdownMenuItem
          id="attachment-menu-microsoft"
          className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
          disabled={disabled}
          onSelect={openMicrosoft}
        >
          <MicrosoftGlyph className="!size-4" />
          <div className="min-w-0">
            <div className="text-sm font-medium">{labels.addMicrosoft}</div>
            <div className="truncate text-xs text-muted-foreground">
              {labels.microsoftHint}
            </div>
          </div>
        </DropdownMenuItem>
      ) : null}
      */}
      {enabledConnectors.github ? (
        <DropdownMenuItem
          id="attachment-menu-github"
          className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
          disabled={disabled}
          onSelect={openGithub}
        >
          <GitHub className="!size-4 fill-current text-muted-foreground" />
          <div className="min-w-0">
            <div className="text-sm font-medium">{labels.addGithub}</div>
            <div className="truncate text-xs text-muted-foreground">
              {labels.githubHint}
            </div>
          </div>
        </DropdownMenuItem>
      ) : null}
      {!Object.values(enabledConnectors).some(Boolean) ? (
        <DropdownMenuItem
          id="attachment-menu-manage-connectors"
          className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
          onSelect={openConnectorSettings}
        >
          <Settings className="!size-4 text-muted-foreground" />
          <span>{labels.manageConnectors}</span>
        </DropdownMenuItem>
      ) : null}
    </>
  );

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            ref={plusRef}
            id="composer-plus-button"
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={labels.add}
            aria-haspopup="menu"
            className="size-10 shrink-0 rounded-full bg-transparent"
          >
            <Plus className="!size-6" strokeWidth={2} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          sideOffset={10}
          collisionPadding={16}
          className="max-h-[min(28rem,calc(100dvh-2rem))] w-[min(20rem,calc(100vw-2rem))] overflow-y-auto rounded-xl border-0 bg-popover p-1.5 shadow-lg md:w-72"
        >
          {isMobile && mobileMenuView === 'connectors' ? (
            <>
              <DropdownMenuItem
                id="attachment-menu-connectors-back"
                className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
                onSelect={(event) => {
                  event.preventDefault();
                  setMobileMenuView('main');
                }}
              >
                <ChevronLeft className="!size-4 text-muted-foreground" />
                <span className="font-medium">{labels.connectors}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {connectorOptions}
            </>
          ) : (
            <>
              {fileUploadEnabled ? (
                <DropdownMenuItem
                  id="attachment-menu-add-files"
                  className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
                  disabled={disabled}
                  onSelect={openFilePicker}
                >
                  <PaperClip className="!size-4 text-muted-foreground" />
                  <span>{labels.addFiles}</span>
                </DropdownMenuItem>
              ) : null}
              {isMobile ? (
                <DropdownMenuItem
                  id="attachment-menu-connectors"
                  className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
                  onSelect={(event) => {
                    event.preventDefault();
                    setMobileMenuView('connectors');
                  }}
                >
                  <Cable className="!size-4 text-muted-foreground" />
                  <span>{labels.connectors}</span>
                  <ChevronRight className="ml-auto !size-4 text-muted-foreground" />
                </DropdownMenuItem>
              ) : (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    id="attachment-menu-connectors"
                    className="cursor-pointer gap-3 rounded-lg px-2.5 py-2.5"
                  >
                    <Cable className="!size-4 text-muted-foreground" />
                    <span>{labels.connectors}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="w-64 rounded-xl border-0 bg-popover p-1.5 shadow-lg">
                      {connectorOptions}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
              {studyCommands.length > 0 ? <DropdownMenuSeparator /> : null}
              {studyCommands.map((command) => {
                const translation = isEnglish
                  ? COMMAND_TRANSLATIONS[command.id]
                  : null;
                const label = translation?.label || command.id;
                const description =
                  translation?.description || command.description || '';
                const isSelected = selectedCommandId === command.id;

                return (
                  <DropdownMenuItem
                    key={command.id}
                    id={`composer-plus-${command.id}`}
                    className={`cursor-pointer gap-3 rounded-lg px-2.5 py-2.5 ${
                      isSelected ? 'bg-accent' : ''
                    }`}
                    disabled={disabled}
                    onSelect={() =>
                      onCommandSelect(isSelected ? undefined : command)
                    }
                  >
                    <Icon
                      name={command.icon}
                      className="!size-4 shrink-0 text-muted-foreground"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight">
                        {label}
                      </div>
                      {description ? (
                        <div className="mt-0.5 text-xs leading-snug text-muted-foreground">
                          {description}
                        </div>
                      ) : null}
                    </div>
                    {isSelected ? (
                      <Check className="!size-4 shrink-0 text-[#08f] dark:text-[#48AAFF]" />
                    ) : (
                      <span className="!size-4 shrink-0" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <input className="hidden" {...upload.getInputProps()} />

      {/* Waiting for approval from FON.
      <MicrosoftButton
        hideTrigger
        anchorRef={plusRef}
        open={msOpen}
        onOpenChange={handleMsOpenChange}
        disabled={disabled}
        onFileUpload={onFileUpload}
        onFileUploadError={onFileUploadError}
      />
      */}
      <GitHubPickerButton
        hideTrigger
        open={githubOpen}
        onOpenChange={handleGithubOpenChange}
        disabled={disabled}
        onRepoSelected={onRepoSelected}
        onRepoError={onRepoError}
      />
    </>
  );
}
